// ==============================================================================
// TRANSITION SYSTEM (The "How")
// Defines how state changes are interpolated over time.
// ==============================================================================

import { WidgetConfig, WidgetState } from './widget-config.model';

/** The nature of the transition, used for headless skipping */
export type TransitionType = 'numeric' | 'color' | 'camera' | 'style' | 'none';

/** Easing function name (standard CSS or library specific) */
export type EasingFunction = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'elastic' | 'bounce';

export interface TransitionProfile {
  /** Duration in ms */
  duration: number;
  /** Easing function */
  easing: EasingFunction;
  /** The nature of the transition */
  type: TransitionType;
  /** Optional delay before starting */
  delay?: number;
}

// ==============================================================================
// ACTION SYSTEM (The "What")
// The Command Pattern. Decouples intent from implementation.
// ==============================================================================

export interface ActionDefinition<ActionType extends string = string> {
  /**
   * The semantic command.
   * Standard: 'PATCH_STYLE', 'TRANSFORM_VIEW', 'RESET_VIEW', 'FOCUS_REGION'
   * Math: 'UPDATE_PARAM', 'FIT_TO_OBJECTS'
   */
  action: ActionType;
  /**
   * Data required to execute the action.
   * - For PATCH_STYLE: { strokeWidth: 5, target: '@SELF' }
   * - For TRANSFORM_VIEW: { mode: 'zoom_cursor', factor: 0.1 }
   */
  payload?: Record<string, unknown>;
  /**
   * How this specific action should be animated.
   * Can be a string ID referencing `behavior.transitions` OR a raw object.
   */
  transition?: string | Partial<TransitionProfile>;
}

export type LifecycleActionDefinition =
  | { action: 'init'; payload: WidgetConfig }
  | { action: 'update'; payload: Partial<WidgetConfig> }
  | { action: 'setState'; payload: WidgetState }
  | { action: 'getState'; payload: undefined };

export type LifecycleActionType = LifecycleActionDefinition['action'];

const LIFECYCLE_ACTIONS = ['init', 'update', 'setState', 'getState'] as const satisfies LifecycleActionType[];

export function isLifecycleAction(
  action: LifecycleActionDefinition | ActionDefinition,
): action is LifecycleActionDefinition {
  return LIFECYCLE_ACTIONS.includes(action.action as LifecycleActionType);
}

/** Result of executing an action */
export type ActionResult = { success: true } | { success: false; error: string };
